import { omit } from '@luminovo/commons';
import * as React from 'react';
import type { JSX } from 'react';

import { Radio, RadioProps } from '../../Radio/Radio';
import { inputSlotWithRef } from '../FieldController/inputSlotWithRef';

export type FieldRadioProps<TValue> = Omit<RadioProps, 'onChange'> & {
    fieldValue: TValue;
    onChange: (value: TValue) => void;

    /** Called after the value changed; `onChange` already updates it, so don't use this for that. */
    onValueChange?: (value: TValue) => void;

    /** react-hook-form's field ref, from `FieldController`. */
    inputRef?: React.Ref<HTMLInputElement>;
};

function FieldRadioInner<TValue>(
    { value, fieldValue, onChange, onValueChange, inputRef, slotProps, ...rest }: FieldRadioProps<TValue>,
    outerRef: React.ForwardedRef<HTMLButtonElement>,
): JSX.Element {
    return (
        <Radio
            ref={outerRef}
            checked={fieldValue === value}
            value={fieldValue}
            onChange={() => {
                onChange(fieldValue);
                onValueChange?.(fieldValue);
            }}
            slotProps={{ ...slotProps, input: inputSlotWithRef(slotProps?.input, inputRef) }}
            {...omit(rest, 'error', 'helperText')}
        />
    );
}

export const FieldRadio = React.forwardRef(FieldRadioInner) as <TValue>(
    props: FieldRadioProps<TValue> & { ref?: React.ForwardedRef<HTMLButtonElement> },
) => JSX.Element;
